import React, { FunctionComponent, useState } from 'react';
import { useQuery, useMutation } from '@apollo/react-hooks';
import { useLocation, Redirect } from 'react-router-dom';
import withStyles from 'react-jss';
import { pathOr } from 'ramda';
import queryString from 'query-string';
import {
  Grid,
  GridRow,
  GridColumn,
  Container,
  Button,
} from 'semantic-ui-react';

import { ClassProp } from 'helpers/types';
import { useCategories, FoundCategory } from 'helpers/useCategories';
import { ChartPlaceholder } from 'components/Placeholder';
import Query from 'components/Query';
import { toastMapping } from 'components/ErrorBoundary/ErrorBoundary';

import {
  GET_BRAND_ASSETS_RAW,
  GET_ACCOUNT_DETAILS,
  APPROVE_BRAND_ASSETS,
  RESOLVE_ZENDESK_TICKET,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.queries';
import {
  Merchant,
  AccountDetails,
  ApproveAssetResponse,
  Values,
  BrandAsset,
  getImageAsset,
  ResolveZendeskTicketResponse,
  getApolloError,
  getCategoryText,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.helpers';
import styles from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantBrandAssets/MerchantBrandAssets.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  merchant: Merchant;
  classes: Styles;
};

type QueryResponse = {
  loading: boolean;
  data: {
    brand: BrandAsset;
  };
};

const renderAssets = (
  accountDetails: AccountDetails,
  classes: Styles,
  handleAssetApproval: Function,
  setUpdatedAt: Function,
  isSubmitting: boolean,
  foundCategory?: FoundCategory,
  foundSubCategory?: FoundCategory,
) => (response: QueryResponse) => {
  if (response.loading) {
    return <ChartPlaceholder className={classes.loading} />;
  }

  const brandAssets = pathOr('', ['data', 'brand'], response);
  const bundle = pathOr('', ['rawAssets', 'bundle'], brandAssets);
  const categoryText = foundCategory
    ? getCategoryText(
        ['getCategoryById', 'foundCategory', 'text'],
        foundCategory,
      )
    : '';

  const subCategoryText = foundSubCategory
    ? getCategoryText(
        ['getCategoryById', 'foundCategory', 'text'],
        foundSubCategory,
      )
    : '';

  const handleClick = () => handleAssetApproval();
  setUpdatedAt(pathOr('', ['rawAssets', 'updatedAt'], brandAssets));

  return (
    <Container className={classes.container}>
      <Grid className={classes.grid}>
        <GridRow className={classes.gridRow}>
          <Grid.Column width={6}>
            <div className={classes.clientWrapper}>
              <img
                className={classes.clientLogo}
                src={getImageAsset('icon', bundle) as string}
              />
              <div className={classes.clientInfo}>
                <h3 className={classes.businessName}>{brandAssets.name}</h3>
                <span>{accountDetails.address1}</span>
                <span>{accountDetails.address2}</span>
                <span className={classes.spacer}>
                  {`${accountDetails.city}, ${accountDetails.state} ${accountDetails.postal} `}
                </span>
                <div className={classes.fieldGroup}>
                  <label className={classes.label}>Locations:</label>
                  {accountDetails.numberOfStores}
                </div>
              </div>
            </div>
          </Grid.Column>
          <Grid.Column width={5} className={classes.gridColumn}>
            <div className={classes.fieldGroup}>
              <label className={classes.label}>Business Category:</label>
              {categoryText}
            </div>
            <div className={classes.fieldGroup}>
              <label className={classes.label}>Business Sub-Category:</label>
              {subCategoryText}
            </div>
            <div className={classes.fieldGroup}>
              <label className={classes.label}>Point of Sale System:</label>
              {accountDetails.pointOfSaleSystem}
            </div>
          </Grid.Column>
          <Grid.Column width={5} className={classes.gridColumn}>
            <div className={classes.fieldGroup}>
              <label className={classes.label}>Contact:</label>
              {`${accountDetails.firstName} ${accountDetails.lastName}`}
            </div>
            <div className={classes.fieldGroup}>
              <label className={classes.label}>Title:</label>
              {accountDetails.title}
            </div>
            <div className={classes.fieldGroup}>
              <div>
                <a href="mailto:#">{accountDetails.email}</a>
              </div>
              <div>{accountDetails.phone}</div>
            </div>
          </Grid.Column>
        </GridRow>
        <GridRow>
          <GridColumn width="12">
            <div>{brandAssets.description}</div>
          </GridColumn>
        </GridRow>
        <GridRow>
          <img
            className={classes.banner}
            src={getImageAsset('banner', bundle) as string}
          />
        </GridRow>
        <GridRow>
          <Button className={classes.button} onClick={handleClick}>
            {isSubmitting ? '...Submitting' : 'Approve Assets'}
          </Button>
        </GridRow>
      </Grid>
    </Container>
  );
};

const BrandAssetApprovalT3: FunctionComponent<Props> = ({
  classes,
  merchant,
}) => {
  const location = useLocation();
  const { merchantId, merchantName, brandId } = merchant;
  const [accountDetails, setAccountDetails] = useState<AccountDetails | {}>({});
  const [updatedAt, setUpdatedAt] = useState<string>();
  const [assetsApproved, setAssetsApproved] = useState<boolean>(false);

  const { ticketId = '' }: Values = queryString.parse(location.search);

  const {
    getCategoryById,
    getSubCategoryById,
    foundCategory,
    foundSubCategory,
  } = useCategories();
  const doCompleted = (data: any): void => {
    const accountDetails = pathOr({}, ['getAccountDetails'], data);
    const { category, subCategory } = accountDetails;

    setAccountDetails(accountDetails);
    getCategoryById({
      variables: {
        params: {
          id: category,
        },
      },
    });
    if (subCategory) {
      getSubCategoryById({
        variables: {
          params: {
            id: subCategory,
          },
        },
      });
    }
  };

  const handleAssetApprovalResponse = (
    approveAssetResponse: ApproveAssetResponse,
  ) => {
    const approvalSuccess = pathOr(
      false,
      ['approveBrandAsset', 'success'],
      approveAssetResponse,
    );

    if (approvalSuccess && ticketId) {
      const params = {
        ticketId,
        status: 'solved',
      };
      resolveZendeskTicket({
        variables: { params },
      });
    } else {
      setAssetsApproved(true);
    }
  };

  const handleAssetApproval = () =>
    approveAssets({
      variables: {
        updatedAt,
        brandId,
        bundle: 'DEFAULT_BRANDING',
      },
    });

  const handleError = (error: any) => {
    toastMapping({}, 'error')(getApolloError(error) as any);
  };

  const handleResolveTicketResponse = (
    resolveZendeskTicketResponse: ResolveZendeskTicketResponse,
  ) => {
    const updateSuccess = pathOr(
      false,
      ['updateTicket', 'success'],
      resolveZendeskTicketResponse,
    );

    if (updateSuccess) setAssetsApproved(true);
  };

  const [approveAssets, { loading: isSubmitting }] = useMutation(
    APPROVE_BRAND_ASSETS,
    {
      onCompleted: handleAssetApprovalResponse,
      onError: handleError,
    },
  );

  const [resolveZendeskTicket, { loading: isResolving }] = useMutation(
    RESOLVE_ZENDESK_TICKET,
    {
      onCompleted: handleResolveTicketResponse,
      onError: handleError,
    },
  );

  const { loading } = useQuery(GET_ACCOUNT_DETAILS, {
    onCompleted: doCompleted,
    variables: {
      params: {
        merchantUuid: merchantId,
      },
    },
  });

  if (loading) return null;

  if (assetsApproved) {
    return <Redirect to={`/merchant/${merchantId}/assets/${merchantName}`} />;
  }

  return (
    <Query
      enableProgressInfo
      query={GET_BRAND_ASSETS_RAW}
      render={renderAssets(
        accountDetails as AccountDetails,
        classes,
        handleAssetApproval,
        setUpdatedAt,
        isSubmitting || isResolving,
        foundCategory,
        foundSubCategory,
      )}
      variables={{
        id: brandId,
      }}
    />
  );
};

export default withStyles(styles)(BrandAssetApprovalT3);
